/**
 * Admin Audit Logs Screen.
 * Lists admin audit trail entries.
 */

import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/app/PageHeader';
import { LoadingOverlay } from '@/app/Spinner';
import { EmptyState } from '@/app/EmptyState';
import { Pagination } from '@/app/Pagination';
import { Modal } from '@/app/Modal';
import { classifyError } from '@/api/errors';
import * as auditApi from '@/api/auditApi';
import type { AuditLog } from '@/api/types';

export function AuditLogsScreen() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const limit = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await auditApi.listAuditLogs({
        action: actionFilter || undefined,
        target_type: targetTypeFilter || undefined,
        limit,
        cursor: page > 1 ? String((page - 1) * limit) : undefined,
      });
      setLogs(result.data);
      setTotal(result.pagination.total);
      setHasMore(result.pagination.has_more);
    } catch (err) {
      setError(classifyError(err).message);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, targetTypeFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="page-content">
      <PageHeader title="Audit Logs" subtitle="Track all admin actions and changes." />

      {/* Filter Bar */}
      <div className="filter-bar">
        <select
          className="select"
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Actions</option>
          <option value="user.status.updated">User Status Updated</option>
          <option value="report.status.updated">Report Status Updated</option>
          <option value="admin.created">Admin Created</option>
          <option value="admin.role.updated">Admin Role Updated</option>
          <option value="system.config.updated">System Config Updated</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
        </select>

        <select
          className="select"
          value={targetTypeFilter}
          onChange={(e) => {
            setTargetTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Target Types</option>
          <option value="user">User</option>
          <option value="report">Report</option>
          <option value="admin">Admin</option>
          <option value="system">System</option>
          <option value="session">Session</option>
        </select>
      </div>

      {/* Error Banner */}
      {error && <div className="alert alert--error mb-16">{error}</div>}

      {/* Loading / Empty */}
      {loading ? (
        <LoadingOverlay />
      ) : logs.length === 0 ? (
        <EmptyState message="No audit logs found." />
      ) : (
        <>
          <div className="card">
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>IP</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="muted text-sm">{formatDate(log.created_at)}</td>
                      <td>{log.admin?.user?.username ?? log.admin_id.slice(0, 8) + '...'}</td>
                      <td>
                        <span className="pill pill--primary">{log.action}</span>
                      </td>
                      <td className="muted">
                        {log.target_type && log.target_id ? (
                          <>
                            {log.target_type} <br />
                            <span className="text-sm">{log.target_id.slice(0, 8)}...</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="muted text-sm">
                        {log.ip_hash ? log.ip_hash.slice(0, 8) + '...' : '—'}
                      </td>
                      <td>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Audit Log Detail Modal */}
      <Modal
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Audit Log Details"
        footer={
          <button className="btn btn--ghost" onClick={() => setSelectedLog(null)}>
            Close
          </button>
        }
      >
        {selectedLog && (
          <div className="stack">
            <div className="field">
              <label>Log ID</label>
              <input className="input" value={selectedLog.id} readOnly />
            </div>

            <div className="row gap-8">
              <div className="field flex-1">
                <label>Admin</label>
                <input
                  className="input"
                  value={selectedLog.admin?.user?.username ?? selectedLog.admin_id}
                  readOnly
                />
              </div>
              <div className="field flex-1">
                <label>Action</label>
                <span className="pill pill--primary">{selectedLog.action}</span>
              </div>
            </div>

            <div className="row gap-8">
              <div className="field flex-1">
                <label>Target Type</label>
                <input className="input" value={selectedLog.target_type ?? '—'} readOnly />
              </div>
              <div className="field flex-1">
                <label>Target ID</label>
                <input className="input" value={selectedLog.target_id ?? '—'} readOnly />
              </div>
            </div>

            {selectedLog.metadata_json && (
              <div className="field">
                <label>Metadata</label>
                <textarea
                  className="textarea"
                  value={JSON.stringify(JSON.parse(selectedLog.metadata_json), null, 2)}
                  readOnly
                  style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 12 }}
                />
              </div>
            )}

            <div className="row gap-8">
              <div className="field flex-1">
                <label>IP Hash</label>
                <input className="input" value={selectedLog.ip_hash ?? '—'} readOnly />
              </div>
              <div className="field flex-1">
                <label>Timestamp</label>
                <input className="input" value={formatDate(selectedLog.created_at)} readOnly />
              </div>
            </div>

            {selectedLog.user_agent && (
              <div className="field">
                <label>User Agent</label>
                <textarea
                  className="textarea"
                  value={selectedLog.user_agent}
                  readOnly
                  style={{ minHeight: 60, fontSize: 12 }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
